import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

/**
 * CandlestickChart — Interactive OHLC candlestick chart using lightweight-charts.
 * Falls back to a recharts area chart if lightweight-charts isn't available.
 */
const CandlestickChart = ({ data, timeframe }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    if (!data || data.length === 0 || !chartContainerRef.current) return;

    let disposed = false;
    let resizeHandler = null;

    const initChart = async () => {
      try {
        const { createChart, ColorType } = await import('lightweight-charts');

        // If component unmounted during async import, bail out
        if (disposed) return;

        // Clear previous chart
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
        }

        const container = chartContainerRef.current;
        if (!container) return;

        const chart = createChart(container, {
          width: container.clientWidth,
          height: 320,
          layout: {
            background: { type: ColorType.Solid, color: 'transparent' },
            textColor: '#8b949e',
            fontSize: 11,
          },
          grid: {
            vertLines: { color: '#21262d' },
            horzLines: { color: '#21262d' },
          },
          crosshair: {
            mode: 0, // Normal crosshair
          },
          rightPriceScale: {
            borderColor: '#21262d',
          },
          timeScale: {
            borderColor: '#21262d',
            timeVisible: timeframe === '1D' || timeframe === '5D',
          },
        });

        // Candlestick series
        const candleSeries = chart.addCandlestickSeries({
          upColor: '#3fb950',
          downColor: '#f85149',
          borderUpColor: '#3fb950',
          borderDownColor: '#f85149',
          wickUpColor: '#3fb950',
          wickDownColor: '#f85149',
        });

        const candleData = data.map((d) => ({
          time: d.date,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        }));
        candleSeries.setData(candleData);

        // Volume series
        const volumeSeries = chart.addHistogramSeries({
          priceFormat: { type: 'volume' },
          priceScaleId: '',
        });
        volumeSeries.priceScale().applyOptions({
          scaleMargins: { top: 0.8, bottom: 0 },
        });

        const volumeData = data.map((d) => ({
          time: d.date,
          value: d.volume,
          color: d.close >= d.open ? 'rgba(63,185,80,0.3)' : 'rgba(248,81,73,0.3)',
        }));
        volumeSeries.setData(volumeData);

        chart.timeScale().fitContent();
        chartRef.current = chart;

        // Resize handler checks disposed flag
        resizeHandler = () => {
          if (!disposed && chartRef.current && container) {
            chartRef.current.applyOptions({ width: container.clientWidth });
          }
        };
        window.addEventListener('resize', resizeHandler);
      } catch (err) {
        console.error('Failed to load lightweight-charts, using fallback:', err);
        if (!disposed) setUseFallback(true);
      }
    };

    initChart();

    return () => {
      disposed = true;
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [data, timeframe]);

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          height: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Price data unavailable
        </Typography>
      </Box>
    );
  }

  // Fallback: recharts area chart if lightweight-charts failed to load
  if (useFallback) {
    const chartColor = data[data.length - 1]?.close >= data[0]?.close ? '#3fb950' : '#f85149';
    return (
      <Box sx={{ width: '100%', height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="candleFallbackGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={0.2} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              tick={{ fontSize: 11, fill: '#8b949e' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={['auto', 'auto']}
              tick={{ fontSize: 11, fill: '#8b949e' }}
              axisLine={false}
              tickLine={false}
              width={50}
              tickFormatter={(val) => `$${val}`}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#161b22', border: '1px solid #21262d', borderRadius: 8 }}
              labelStyle={{ color: '#8b949e' }}
              itemStyle={{ color: '#e6edf3' }}
              formatter={(value) => [`$${value.toFixed(2)}`, 'Close']}
              labelFormatter={(label) => new Date(label).toLocaleDateString()}
            />
            <Area
              type="monotone"
              dataKey="close"
              stroke={chartColor}
              strokeWidth={2}
              fill="url(#candleFallbackGrad)"
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  return (
    <Box
      ref={chartContainerRef}
      sx={{
        width: '100%',
        height: 320,
      }}
    />
  );
};

export default CandlestickChart;
